import type { DealTransformed } from "@/lib/schema/deal-schema";
import { Check, ExternalLink, Globe, Linkedin, Loader2, Pencil, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { aiApi } from "@/api/ai-api";
import { editDealInfo, editSource } from "@/api/deals-api";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useCurrentDealStore } from "@/store/current-deal-store";
import { getErrorMessage } from "@/lib/api-error";
import { toast } from "sonner";
import {
  BusinessModels,
  CountryOptions,
  RevenueModels,
  StageOptions,
  cn,
  getStageText,
} from "@/lib/utils";
import { memo, useEffect, useState } from "react";
import { CompanyAvatar } from "@/components/deals/company-avatar";
import {
  EditableComboboxField,
  EditableMultiBadgeField,
  EditableSelectField,
  EditableTextField,
  EditableTextareaField,
  type FieldSuggestion,
} from "@/components/deal-detail/fields/editable-primitives";
import {
  FieldStatusDot,
  type FieldStatus,
} from "@/components/deal-detail/fields/editable-field";
import {
  EditableIndustryField,
  resolveIndustryCodeName,
} from "@/components/deal-detail/fields/editable-industry";
import { EditableFundraisingField } from "@/components/deal-detail/fields/editable-fundraising";
import { useEditDealField } from "@/components/deal-detail/fields/use-edit-deal-field";
import {
  BulkEditProvider,
  useBulkEdit,
} from "@/components/deal-detail/fields/bulk-edit-context";

interface ProfileTabProps {
  currentDeal: DealTransformed;
  /**
   * Report status from the deals layout. Used to surface the
   * Re-enrich processing state — when `report_status.deal_info` is
   * `in_progress`, the header swaps to "⟳ Re-enriching…".
   */
  reportStatus?: unknown;
}

/**
 * Canonical list of `field_name` entries the Profile tab renders as
 * editable fields. Used to scope the pending-review count so the tab
 * badge and the in-tab ReviewHead only reflect fields the user can
 * actually act on from this tab (not Deal Info fields, etc.).
 */
export const PROFILE_TAB_FIELD_NAMES = [
  "company_name",
  "short_bio",
  "stage",
  "sub_industries",
  "business_model",
  "revenue_models",
  "address",
  "company_website",
  "linkedIn",
  "current_funding_round",
  "previous_funding_rounds",
] as const;

const PLACEHOLDER = new Set([
  "",
  "n/a",
  "na",
  "unknown",
  "not specified",
  "no information provided",
  "no information",
  "information not provided",
]);
/**
 * Loose equality for staged-vs-current dealInfo comparisons. Coerces
 * scalars to strings (handles "1.1" vs 1.1), sorts arrays before
 * stringifying (order-insensitive for things like sub_industries), and
 * JSON-stringifies objects. Treats null/undefined/"" as equivalent.
 */
function valuesEqual(a: unknown, b: unknown): boolean {
  // Treat AI placeholders ("N/A", "Unknown", …) as equivalent to empty so a
  // field the FE renders as blank doesn't get re-saved as "" when the user
  // opens bulk-edit and touches a different row.
  if (isBlank(a) && isBlank(b)) return true;
  const norm = (v: unknown): string => {
    if (v === null || v === undefined) return "";
    if (Array.isArray(v))
      return JSON.stringify([...v].map((x) => norm(x)).sort());
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  return norm(a) === norm(b);
}

const isBlank = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return PLACEHOLDER.has(v.trim().toLowerCase());
  if (Array.isArray(v)) return v.length === 0 || v.every(isBlank);
  if (typeof v === "object") {
    const values = Object.values(v as Record<string, unknown>);
    return values.length === 0 || values.every(isBlank);
  }
  // Numbers, booleans, etc. — treat 0 / false as blank since the AI
  // emits those as "no data" placeholders for things like total_rounds.
  if (typeof v === "number") return v === 0;
  if (typeof v === "boolean") return !v;
  return false;
};

/** Count Profile-tab fields currently awaiting human review. */
export function getProfilePendingCount(deal: DealTransformed): number {
  const rendered = new Set<string>(PROFILE_TAB_FIELD_NAMES);
  return (
    deal.dealInfo.deal?.filter((f) => {
      if (f.field_state !== "pending") return false;
      if (!rendered.has(f.field_name)) return false;
      // Placeholder values ("N/A", "No information provided", empty objects,
      // …) have no AI value to bulk-approve, so they drop out of the verify
      // badge — even though the field's dot may still read as pending when it
      // has AI suggestions the user can pick from.
      if (isBlank(f.field_value)) return false;
      return true;
    }).length ?? 0
  );
}

function Legend() {
  const items: Array<{ status: FieldStatus; label: string }> = [
    { status: "approved", label: "Approved" },
    { status: "pending", label: "Pending review" },
    { status: "missing", label: "Missing" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1.5">
          <FieldStatusDot status={i.status} />
          {i.label}
        </span>
      ))}
    </div>
  );
}

/**
 * Aggregate review-status indicator. Mirrors the dgpt-prototype's
 * EnrichmentHead pattern: show the count of pending fields with a
 * one-click bulk-approve, or a subtle "all verified" when clean.
 */
function ReviewHead({
  pending,
  onVerifyAll,
  isVerifying,
}: {
  pending: number;
  onVerifyAll: () => void;
  isVerifying: boolean;
}) {
  if (pending === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        All verified
      </span>
    );
  }
  return (
    <div className="flex items-center gap-3 text-[11px]">
      <span>
        <span className="font-mono tabular-nums font-semibold text-amber-600">
          {pending}
        </span>
        <span className="text-muted-foreground"> need review</span>
      </span>
      <button
        type="button"
        onClick={onVerifyAll}
        disabled={isVerifying}
        className={cn(
          "underline underline-offset-4 decoration-muted-foreground/40 text-foreground/75 transition-colors",
          "hover:decoration-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
        )}
      >
        {isVerifying ? "Verifying…" : "Verify all"}
      </button>
    </div>
  );
}

const SUGGESTION_FIELD_LABEL: Record<string, string> = {
  linkedIn: "LinkedIn",
  company_website: "website",
};

function SuggestionChips({
  fieldName,
  suggestions,
}: {
  fieldName: string;
  suggestions: FieldSuggestion[];
}) {
  const { save } = useEditDealField();
  // Tapping a chip opens a confirmation modal instead of applying instantly,
  // so a stray/mis-tap can't overwrite the field — the user reviews the link
  // and confirms before it's saved.
  const [active, setActive] = useState<FieldSuggestion | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  if (suggestions.length === 0) return null;

  const label = SUGGESTION_FIELD_LABEL[fieldName] ?? "value";
  const cleanUrl = (url: string) =>
    url.replace(/^https?:\/\//, "").replace(/\/$/, "");

  const handleApply = async () => {
    if (!active || isApplying) return;
    setIsApplying(true);
    try {
      await save(fieldName, active.url);
      setActive(null);
    } catch {
      // hook fires its own error toast
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground/60">
          Suggested
        </span>
        {suggestions.slice(0, 3).map((s, i) => (
          <button
            key={`${s.url}-${i}`}
            type="button"
            onClick={() => setActive(s)}
            className="flex w-full min-w-0 items-start gap-1.5 rounded-md border border-dashed border-border bg-muted/30 px-2 py-1 font-mono text-[11px] text-muted-foreground transition-colors hover:border-foreground/40 hover:bg-muted hover:text-foreground sm:inline-flex sm:w-auto sm:max-w-[260px] sm:items-center sm:rounded-full sm:py-0.5"
            title={s.title ? `${s.title}\n${s.url}` : s.url}
          >
            {s.favicon ? (
              <img
                src={s.favicon}
                alt=""
                className="mt-0.5 h-3 w-3 shrink-0 sm:mt-0"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : null}
            <span className="min-w-0 break-all text-left sm:truncate sm:break-normal sm:text-center">
              {cleanUrl(s.url)}
            </span>
          </button>
        ))}
      </div>

      <Dialog
        open={!!active}
        onOpenChange={(open) => {
          if (!open && !isApplying) setActive(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Use this {label}?</DialogTitle>
            <DialogDescription>
              Review the link below, then apply it to the {label} field.
            </DialogDescription>
          </DialogHeader>
          {active && (
            <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
              <div className="flex items-center gap-2">
                {active.favicon ? (
                  <img
                    src={active.favicon}
                    alt=""
                    className="h-4 w-4 shrink-0"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display =
                        "none";
                    }}
                  />
                ) : null}
                <span className="text-sm font-medium text-foreground">
                  {active.title || cleanUrl(active.url)}
                </span>
              </div>
              <a
                href={active.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 break-all font-mono text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                {active.url}
                <ExternalLink className="h-3 w-3 shrink-0" />
              </a>
              {active.description ? (
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {active.description}
                </p>
              ) : null}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActive(null)}
              disabled={isApplying}
            >
              Cancel
            </Button>
            <Button onClick={handleApply} disabled={isApplying}>
              {isApplying ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="mr-1.5 h-3.5 w-3.5" />
              )}
              Use this {label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="border-b border-border px-4 py-2.5">
        <span className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/60">
          {title}
        </span>
      </div>
      <div className="divide-y divide-border/60">{children}</div>
    </div>
  );
}

// User-attribution values for the Source dropdown. System-stamped channel
// markers are excluded — see SYSTEM_SOURCE_MARKERS below.
const USER_SOURCE_OPTIONS = [
  "Submission",
  "Email",
  "Intro",
  "Referral",
  "CoInvestor",
  "Angel",
  "LP",
  "Accelerator",
  "Conference",
  "Platform",
  "Advisor",
  "TechTransfer",
  "Grant",
  "Social",
  "Media",
  "Manual",
] as const;

// Source values stamped by the BE at creation. Locked from the dropdown
// so the channel of origin isn't overwritten. The "|" branch catches
// combined "<companySource> | <marker>" values from _explore.
const SYSTEM_SOURCE_MARKERS = ["DealRadar", "RaiseBoard", "Portal", "Demo"];
function isSystemStampedSource(value: string | null | undefined): boolean {
  if (!value) return false;
  if (value.includes(" | ")) return true;
  return SYSTEM_SOURCE_MARKERS.some((m) => value === m);
}

const SOURCE_OPTIONS = USER_SOURCE_OPTIONS.map((s) => ({ value: s, label: s }));

function EditableSourceField({ value }: { value: string | null }) {
  if (isSystemStampedSource(value)) {
    return <ReadField label="Source" value={value} status={null} />;
  }
  // Stages under fieldName="source"; the bulk Save handler routes that
  // key through editSource (not editDealInfo).
  return (
    <EditableComboboxField
      label="Source"
      fieldName="source"
      value={value}
      options={SOURCE_OPTIONS}
      status={value ? null : "missing"}
    />
  );
}

function ReadField({
  label,
  value,
  multiline,
  status,
}: {
  label: string;
  value: React.ReactNode;
  multiline?: boolean;
  status?: FieldStatus;
}) {
  const showEmpty = value === null || value === undefined || value === "";
  return (
    <div
      className={cn(
        "flex px-4",
        multiline
          ? "flex-col gap-4 py-3"
          : "flex-col gap-1.5 py-2 sm:flex-row sm:items-center sm:gap-4"
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground",
          multiline ? "" : "sm:w-36"
        )}
      >
        {label}
        <FieldStatusDot status={status ?? null} />
      </span>
      <div
        className={cn(
          "flex-1 text-sm",
          showEmpty ? "text-muted-foreground/40" : "text-foreground"
        )}
      >
        {showEmpty
          ? status === "missing"
            ? "No info available, enter manually"
            : "—"
          : value}
      </div>
    </div>
  );
}

function ProfileTab(props: ProfileTabProps) {
  return (
    <BulkEditProvider>
      <ProfileTabUrlEffects />
      <ProfileTabBody {...props} />
    </BulkEditProvider>
  );
}

export default memo(ProfileTab);

/**
 * Sibling that owns the router-context subscriptions for the Profile tab.
 * Isolating these here means URL changes only re-render this null-returning
 * component — `ProfileTabBody` (heavy tree) stays mounted and untouched.
 */
function ProfileTabUrlEffects() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const bulk = useBulkEdit();

  // Enter bulk-edit mode when the URL carries ?edit=1 (e.g. from the
  // deal header's "Edit deal" action). Strip the param afterwards so a
  // refresh or back-nav doesn't re-trigger. Depends only on searchParams
  // so the effect doesn't re-fire whenever bulk state churns — putting
  // `bulk` in the deps means every Cancel / stage / Save flip would
  // re-evaluate this effect, even with the early-return guard.
  useEffect(() => {
    if (searchParams.get("edit") !== "1") return;
    bulk.reset();
    bulk.setEnabled(true);
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // When the URL carries a #field-* hash (e.g. from the deals list
  // Review-industries badge), scroll that section into view and flash a ring
  // around it. Re-runs on hash change so a second click while already on
  // this tab still triggers the highlight.
  useEffect(() => {
    const hash = location.hash.replace(/^#/, "");
    if (!hash) return;

    const timers: number[] = [];
    let flashTimer: number | undefined;
    let flashedEl: HTMLElement | null = null;

    const scrollNow = () => {
      const el = document.getElementById(hash);
      if (!el) return false;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      return true;
    };

    const runWhenReady = (delay: number, attempts = 0) => {
      const t = window.setTimeout(() => {
        if (!scrollNow() && attempts < 10) {
          runWhenReady(delay, attempts + 1);
        }
      }, delay);
      timers.push(t);
    };

    runWhenReady(100);
    timers.push(window.setTimeout(() => scrollNow(), 600));
    timers.push(
      window.setTimeout(() => {
        const el = document.getElementById(hash);
        if (!el) return;
        scrollNow();
        el.setAttribute("data-flash", "true");
        flashedEl = el;
        flashTimer = window.setTimeout(() => {
          el.removeAttribute("data-flash");
          flashedEl = null;
          // Clear the hash via the router so a second click on the same
          // badge re-fires this effect.
          navigate(location.pathname + location.search, { replace: true });
        }, 2000);
      }, 900),
    );

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      if (flashTimer) window.clearTimeout(flashTimer);
      // If the effect tears down mid-flash (hash changed, component
      // unmounted), strip the attribute so the previous target doesn't
      // keep its ring.
      if (flashedEl) flashedEl.removeAttribute("data-flash");
    };
  }, [location.hash, location.pathname, location.search, navigate]);

  return null;
}

function ProfileTabBody({
  currentDeal,
  reportStatus,
}: ProfileTabProps) {
  const { save } = useEditDealField();
  const { activeWorkspaceId } = useWorkspaceStore();
  const { updateDeal } = useCurrentDealStore();
  const queryClient = useQueryClient();
  const [isVerifyingAll, setIsVerifyingAll] = useState(false);
  const bulk = useBulkEdit();
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  // Holds the "Re-enriching…" state from the moment the trigger succeeds until
  // the socket/poll reports deal_info as in_progress, so the button doesn't
  // flash back to "Re-enrich" in the gap between the two.
  const [awaitingReEnrich, setAwaitingReEnrich] = useState(false);

  // Pull the deal-info report status to drive the Re-enrich processing
  // state. Pipeline emits `in_progress` (and legacy `inprogress`) /
  // `pending` while it's running, and `completed` / `failed` when done.
  const dealInfoStatus = (() => {
    if (!reportStatus || typeof reportStatus !== "object") return undefined;
    const rs = reportStatus as Record<string, unknown>;
    const app = rs.opportunityApplication as Record<string, unknown> | undefined;
    const reports = app?.report_status as Record<string, string> | undefined;
    return reports?.deal_info;
  })();
  const isReEnrichingFromStatus =
    dealInfoStatus === "in_progress" ||
    dealInfoStatus === "queued" ||
    dealInfoStatus === "inprogress" ||
    dealInfoStatus === "pending";

  const reEnrichMutation = useMutation({
    mutationFn: () => {
      if (!activeWorkspaceId) throw new Error("No workspace selected");
      return aiApi.getDealInfo({
        companyId: currentDeal.id,
        opportunityId: activeWorkspaceId,
        applicationInfo: currentDeal.dealInfo.applicationInfo,
      });
    },
    onSuccess: (data) => {
      // The endpoint can either return synchronously with a fresh
      // deal_info array OR queue the job and return `{ message:
      // "...queued..." }`. The first case is rare; usually the worker
      // streams updates back via the report-status socket.
      if (data?.deal_info) {
        updateDeal({
          dealInfo: {
            ...currentDeal.dealInfo,
            deal: data.deal_info as typeof currentDeal.dealInfo.deal,
          },
        });
      }
      toast.success("Re-enrichment started");
      setAwaitingReEnrich(true);
      // Nudge the report-status query so it flips to in_progress sooner.
      queryClient.invalidateQueries({
        queryKey: ["reportStatus", currentDeal.id, activeWorkspaceId],
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to start re-enrichment"));
    },
  });

  // Hand off the optimistic bridge to the status-driven flag once it catches
  // up, and cover the case where the run finished before in_progress was seen.
  useEffect(() => {
    if (
      isReEnrichingFromStatus ||
      dealInfoStatus === "completed" ||
      dealInfoStatus === "failed"
    ) {
      setAwaitingReEnrich(false);
    }
  }, [isReEnrichingFromStatus, dealInfoStatus]);

  const isReEnriching =
    reEnrichMutation.isPending || isReEnrichingFromStatus || awaitingReEnrich;

  const handleReEnrich = () => {
    if (isReEnriching) return;
    reEnrichMutation.mutate();
  };

  // Only count / verify fields the Profile tab actually renders —
  // so the ReviewHead and the tab-bar badge stay in sync with what
  // the user can act on here. Fields from other tabs (Deal Info,
  // questionnaire, etc.) are excluded even when pending.
  // Pending entries whose value is a placeholder ("N/A", "Unknown",
  // "No information provided", …) have no AI value to bulk-approve, so
  // they drop out of the "needs review" / verify-all count. (Their dot
  // still reads as pending when AI suggestions exist — the user resolves
  // those by picking a suggestion, not by Verify All.)
  const profileFieldSet = new Set<string>(PROFILE_TAB_FIELD_NAMES);
  const pendingFields = (currentDeal.dealInfo.deal ?? []).filter((f) => {
    if (f.field_state !== "pending") return false;
    if (!profileFieldSet.has(f.field_name)) return false;
    if (isBlank(f.field_value)) return false;
    return true;
  });
  const pendingCount = pendingFields.length;

  // Hide the ReviewHead entirely until deal info has actually been
  // generated. Before the first run, pending=0 would otherwise render a
  // misleading "All verified". Gate on the worker's report_status so we
  // only surface the verify UI once a generation has completed and no
  // re-enrichment is in flight.
  const showReviewHead =
    dealInfoStatus === "completed" && !isReEnriching;

  // Debug: when the count we surface doesn't match what the user can see
  // (badge / "N need review" vs purple dots on the rendered fields),
  // dump the raw deal-info entries so we can spot the offending records
  // — typically duplicates with the same field_name but different states,
  // or pending entries with empty/blank field_value.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const allPending = (currentDeal.dealInfo.deal ?? []).filter(
      (f) => f.field_state === "pending",
    );
    const offProfilePending = allPending.filter(
      (f) => !profileFieldSet.has(f.field_name),
    );
    const profilePendingByName = pendingFields.reduce<Record<string, number>>(
      (acc, f) => {
        acc[f.field_name] = (acc[f.field_name] ?? 0) + 1;
        return acc;
      },
      {},
    );
    const duplicates = Object.entries(profilePendingByName).filter(
      ([, n]) => n > 1,
    );

    /* eslint-disable no-console */
    console.groupCollapsed(
      `[ProfileTab] pending review = ${pendingCount} (deal ${currentDeal.id})`,
    );
    console.log(
      "Total pending in dealInfo.deal:",
      allPending.length,
      "/ scoped to Profile tab:",
      pendingCount,
    );
    console.table(
      pendingFields.map((f) => ({
        field_name: f.field_name,
        field_state: f.field_state,
        is_blank:
          f.field_value === null ||
          f.field_value === undefined ||
          (typeof f.field_value === "string" && f.field_value.trim() === "") ||
          (Array.isArray(f.field_value) && f.field_value.length === 0),
        value_type: Array.isArray(f.field_value)
          ? "array"
          : typeof f.field_value,
        value_preview:
          typeof f.field_value === "string"
            ? f.field_value.slice(0, 60)
            : JSON.stringify(f.field_value)?.slice(0, 60),
      })),
    );
    if (duplicates.length > 0) {
      console.warn(
        "Duplicate pending entries (same field_name appears more than once):",
        duplicates,
      );
    }
    if (offProfilePending.length > 0) {
      console.log(
        "Pending fields NOT rendered on this tab:",
        offProfilePending.map((f) => f.field_name),
      );
    }

    console.groupEnd();
    /* eslint-enable no-console */
  }, [
    currentDeal.id,
    currentDeal.dealInfo.deal,
    pendingFields,
    pendingCount,
    profileFieldSet,
  ]);

  const handleStartBulkEdit = () => {
    bulk.reset();
    bulk.setEnabled(true);
  };

  const handleCancelBulkEdit = () => {
    bulk.setEnabled(false);
    bulk.reset();
  };

  const handleSaveBulkEdit = async () => {
    if (!activeWorkspaceId || isBulkSaving) return;

    // Diff against the baseline captured at first-stage (i.e. the value
    // the user saw when bulk-edit opened), not raw dealInfo — otherwise
    // fields seeded from overview fallbacks or placeholders ("N/A", …)
    // appear "changed" without user input and fire spurious saves.
    const changed = Object.entries(bulk.staged).filter(
      ([fieldName, stagedValue]) =>
        !valuesEqual(bulk.baseline[fieldName], stagedValue)
    );

    if (changed.length === 0) {
      toast.message("No changes to save");
      bulk.setEnabled(false);
      bulk.reset();
      return;
    }

    setIsBulkSaving(true);
    // Promise.allSettled so one rejected field doesn't drop the successes —
    // the user sees which writes landed and which didn't (mirrors Verify
    // all). "source" routes through editSource because it's a top-level
    // OpportunityApplication value, not a deal_info entry.
    const settled = await Promise.allSettled(
      changed.map(async ([fieldName, fieldValue]) => {
        if (fieldName === "source") {
          await editSource.editSource(
            currentDeal.id,
            activeWorkspaceId,
            fieldValue as string,
          );
          return { kind: "source" as const, value: fieldValue as string };
        }
        const res = await editDealInfo.editDealInfo({
          workspaceId: activeWorkspaceId,
          companyId: currentDeal.id,
          fieldName,
          fieldValue,
        });
        return { kind: "dealInfo" as const, fieldName, response: res };
      }),
    );
    const succeeded = settled
      .filter((s): s is PromiseFulfilledResult<
        | { kind: "source"; value: string }
        | { kind: "dealInfo"; fieldName: string; response: Awaited<ReturnType<typeof editDealInfo.editDealInfo>> }
      > => s.status === "fulfilled")
      .map((s) => s.value);
    const failedCount = settled.length - succeeded.length;

    // Apply whichever updates landed even if some writes failed — partial
    // success still beats a fully-stale UI.
    const dealInfoWrites = succeeded.filter(
      (
        r
      ): r is {
        kind: "dealInfo";
        fieldName: string;
        response: Awaited<ReturnType<typeof editDealInfo.editDealInfo>>;
      } => r.kind === "dealInfo"
    );
    const sourceUpdate = succeeded.find((r) => r.kind === "source");
    if (currentDeal) {
      const updates: Partial<typeof currentDeal> = {};
      if (dealInfoWrites.length > 0) {
        // Each write returns the full deal_info as of its own commit; because
        // the writes run concurrently a single snapshot can carry a stale
        // sibling field (industry tags stage sub_industries + industries
        // together). Start from the current array and overlay each write's
        // OWN field from its OWN response so every change lands regardless of
        // which request resolved last. Replace in place rather than keying by
        // field_name — deal_info can legitimately hold multiple entries with
        // the same field_name (e.g. duplicate pending records).
        const deal = [...currentDeal.dealInfo.deal];
        for (const w of dealInfoWrites) {
          const respDeal = w.response?.updatedApplication?.deal_info as
            | typeof currentDeal.dealInfo.deal
            | undefined;
          const entry = respDeal?.find((f) => f.field_name === w.fieldName);
          if (!entry) continue;
          const idx = deal.findIndex((f) => f.field_name === w.fieldName);
          if (idx >= 0) deal[idx] = entry;
          else deal.push(entry);
        }
        updates.dealInfo = {
          ...currentDeal.dealInfo,
          deal,
        };
      }
      if (sourceUpdate?.kind === "source") {
        updates.overview = {
          ...currentDeal.overview,
          source: sourceUpdate.value,
        };
      }
      if (Object.keys(updates).length > 0) updateDeal(updates);
    }

    if (failedCount === 0) {
      toast.success("Profile updated", {
        description: `${changed.length} field${
          changed.length === 1 ? "" : "s"
        } saved · re-enriching with latest`,
      });
    } else if (succeeded.length === 0) {
      toast.error(
        `Failed to save ${changed.length} ${
          changed.length === 1 ? "field" : "fields"
        }`,
      );
    } else {
      toast.error(
        `Saved ${succeeded.length} of ${changed.length} fields, ${failedCount} failed`,
      );
    }
    bulk.setEnabled(false);
    bulk.reset();
    setIsBulkSaving(false);
  };

  const handleVerifyAll = async () => {
    if (isVerifyingAll || pendingCount === 0) return;
    setIsVerifyingAll(true);
    const count = pendingFields.length;
    // Promise.allSettled rather than Promise.all — one failed field
    // must not throw away the success state of the others, and we want
    // an accurate summary count for the toast.
    const results = await Promise.allSettled(
      pendingFields.map((f) =>
        save(f.field_name, f.field_value, { silent: true })
      )
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    const succeeded = count - failed;
    if (failed === 0) {
      toast.success(`Verified ${count} ${count === 1 ? "field" : "fields"}`);
    } else if (succeeded === 0) {
      toast.error(
        `Failed to verify ${count === 1 ? "field" : `${count} fields`}`
      );
    } else {
      toast.error(
        `Verified ${succeeded} of ${count} fields, ${failed} failed`
      );
    }
    setIsVerifyingAll(false);
  };

  const dealField = (apiName: string): string | null => {
    const f = currentDeal.dealInfo.deal?.find((d) => d.field_name === apiName);
    const v = f?.field_value;
    if (typeof v === "string" && !isBlank(v)) return v;
    if (Array.isArray(v) && v.length > 0) {
      const joined = v.filter((x) => !isBlank(x)).join(", ");
      return joined || null;
    }
    return null;
  };

  const dealFieldArray = (apiName: string): string[] => {
    const f = currentDeal.dealInfo.deal?.find((d) => d.field_name === apiName);
    const v = f?.field_value;
    if (Array.isArray(v)) return v.filter((x) => !isBlank(x)).map(String);
    if (typeof v === "string" && !isBlank(v))
      return v.split(",").map((s) => s.trim()).filter(Boolean);
    return [];
  };

  /** AI provenance entries for a field (file / URL each value was extracted from). */
  const dealFieldSources = (apiName: string) => {
    const f = currentDeal.dealInfo.deal?.find((d) => d.field_name === apiName);
    return f?.sources;
  };

  /**
   * Resolve review-workflow status for a field based on dealInfo.deal[].field_state.
   *   - "approved" → green
   *   - "pending"  → purple (awaiting human review)
   *   - "missing"  → red (no record AND no fallback value on overview,
   *                       OR a placeholder value like "N/A" /
   *                       "No information provided" / empty object with no
   *                       AI suggestions to act on)
   *   - null       → no dot
   *
   * A placeholder value that DOES have actionable AI suggestions is not
   * "missing" — the user can pick a suggestion — so it keeps its review
   * state (pending → purple) instead of turning red.
   */
  const fieldStatus = (
    apiName: string,
    hasFallback: boolean,
    effectiveValue?: unknown,
  ): FieldStatus => {
    const f = currentDeal.dealInfo.deal?.find((d) => d.field_name === apiName);
    if (f) {
      const checkValue =
        effectiveValue !== undefined ? effectiveValue : f.field_value;
      const valueIsBlank = isBlank(checkValue);

      if (valueIsBlank) {
        if (hasFallback) return null;
        const hasSuggestions = (f.suggestions ?? []).some(
          (s) => !isBlank(s.url),
        );
        if (!hasSuggestions) return "missing";
      }

      if (f.field_state === "approved") return "approved";
      if (f.field_state === "pending") return "pending";
    }
    if (!hasFallback) return "missing";
    return null;
  };

  /**
   * AI-generated suggestions for a field (backend populates during enrichment).
   * Shown inline in the editor so the user can pick one — matches Deal Info's
   * suggestion list UI.
   */
  const dealFieldSuggestions = (apiName: string) => {
    const f = currentDeal.dealInfo.deal?.find((d) => d.field_name === apiName);
    const seen = new Set<string>();
    return (f?.suggestions ?? [])
      .filter((s) => {
        if (isBlank(s.url)) return false;
        const key = s.url.replace(/\/$/, "").toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const overview = currentDeal.overview;

  // Founder/AI placeholders ("No information provided", "N/A", …) can land on
  // the top-level overview fields; route fallbacks through isBlank so a
  // placeholder reads as missing instead of being shown as a real value.
  const overviewFallback = (v: unknown): string | null =>
    typeof v === "string" && !isBlank(v) ? v : null;

  // ── Simple fields (source of truth: dealInfo.deal) ──────────────────────────
  const businessName = dealField("company_name") || overviewFallback(overview.name);
  const description = dealField("short_bio") || overviewFallback(overview.description);

  const stageRecord = currentDeal.dealInfo.deal?.find(
    (d) => d.field_name === "stage",
  );
  // If the AI explicitly returned a placeholder ("N/A", etc.), don't
  // mask it with the overview fallback — the field should read as
  // missing so the user can fill it in. Only use overview when there's
  // no AI record at all.
  const stageAiPlaceholder =
    !!stageRecord && isBlank(stageRecord.field_value);
  const stageRawValue: string | null = (() => {
    if (stageAiPlaceholder) return null;
    const v = stageRecord?.field_value;
    if (typeof v === "string") {
      const trimmed = v.trim();
      return trimmed || null;
    }
    if (typeof v === "number") return getStageText(v);
    return null;
  })();
  // `overview.companyStage` is `getStageText`-canonicalized at the
  // transformer ([services/deals-service.ts]), so it returns "Unknown"
  // when the BE has no stage. Don't treat "Unknown" (or any other
  // placeholder) as a real fallback value.
  const overviewStageFallback =
    overview.companyStage && !isBlank(overview.companyStage)
      ? overview.companyStage
      : "";
  const stageValue: string = stageRawValue ?? overviewStageFallback;
  const stageLabel: string | null = (() => {
    if (!stageValue) return null;
    const match = StageOptions.find(
      (o) => o.value.toLowerCase() === stageValue.toLowerCase(),
    );
    return match?.label ?? stageValue;
  })();

  const businessModelArray = dealFieldArray("business_model");
  const revenueModelArray = dealFieldArray("revenue_models");

  const source = overview.source || null;
  const country = dealField("address") || overviewFallback(overview.country);
  const website = dealField("company_website") || overviewFallback(overview.website);
  const linkedIn = dealField("linkedIn");

  const websiteSuggestions = dealFieldSuggestions("company_website");
  const linkedInSuggestions = dealFieldSuggestions("linkedIn");

  // ── Industry ────────────────────────────────────────────────────────────────
  const subIndustryCodes = dealFieldArray("sub_industries");
  // Same resolver the row itself renders with (10-digit sub-industry OR
  // 8-digit parent code), so the status dot can't read "missing" for codes
  // that do render as tags.
  const resolvableSubIndustryCodes = subIndustryCodes.filter(
    (code) => resolveIndustryCodeName(code) !== null,
  );

  // ── Fundraising ─────────────────────────────────────────────────────────────
  const currentRoundField = currentDeal.dealInfo.deal?.find(
    (f) => f.field_name === "current_funding_round"
  );
  const prevRoundsField = currentDeal.dealInfo.deal?.find(
    (f) => f.field_name === "previous_funding_rounds"
  );
  const currentRound = (currentRoundField?.field_value as
    | { amount?: string; currency?: string }
    | undefined) || { amount: "", currency: "" };
  const prevRoundsData = prevRoundsField?.field_value as
    | {
        previous_funding?: Array<{
          round_type?: string;
          amount?: string;
          currency?: string;
          investors?: string;
          round_date?: string;
          valuation?: string;
        }>;
      }
    | undefined;
  const previousRounds = (prevRoundsData?.previous_funding || []).map((r) => ({
    round_type: r.round_type || "",
    amount: r.amount || "",
    currency: r.currency || "",
    investors: r.investors || "",
    round_date: r.round_date || "",
    valuation: r.valuation || "",
  }));

  const formatAmount = (
    amount?: string,
    currency?: string
  ): string | null => {
    if (!amount || isBlank(amount)) return null;
    return currency && !isBlank(currency) ? `${amount} ${currency}` : amount;
  };

  const currentRoundLabel = formatAmount(
    currentRound.amount,
    currentRound.currency
  );
  const previouslyRaised = (() => {
    const parsed = previousRounds
      .map((r) => {
        if (isBlank(r.amount)) return null;
        const num = Number(String(r.amount).replace(/[^0-9.]/g, ""));
        return Number.isFinite(num) && num > 0
          ? { num, currency: r.currency }
          : null;
      })
      .filter((x): x is { num: number; currency: string } => x !== null);
    if (parsed.length === 0) return null;
    const total = parsed.reduce((s, x) => s + x.num, 0);
    const currency = parsed.find((x) => x.currency && !isBlank(x.currency))
      ?.currency;
    return currency
      ? `${total.toLocaleString()} ${currency}`
      : total.toLocaleString();
  })();
  const latestValuation =
    previousRounds.find((r) => !isBlank(r.valuation))?.valuation || null;

  // ── Website / LinkedIn rich read values ─────────────────────────────────────
  const renderLink = (href: string, Icon: typeof Globe) => (
    <a
      href={href.startsWith("http") ? href : `https://${href}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex min-w-0 items-center gap-1.5 transition-colors hover:text-foreground"
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="min-w-0 truncate">
        {href.replace(/^https?:\/\//, "").replace(/\/$/, "")}
      </span>
    </a>
  );

  // EditableField treats only null/undefined/"" as empty for its `—` /
  // "No info available" placeholder. SuggestionChips itself returns null
  // when there are no suggestions, but the JSX element wrapping it is
  // truthy, so the empty branch is skipped and the cell renders blank.
  // Gate at the call site so an empty-no-suggestions state falls through
  // to the placeholder.
  const websiteRead = website
    ? renderLink(website, Globe)
    : websiteSuggestions.length > 0
      ? (
          <SuggestionChips
            fieldName="company_website"
            suggestions={websiteSuggestions}
          />
        )
      : null;

  const linkedInRead = linkedIn
    ? renderLink(linkedIn, Linkedin)
    : linkedInSuggestions.length > 0
      ? (
          <SuggestionChips
            fieldName="linkedIn"
            suggestions={linkedInSuggestions}
          />
        )
      : null;

  const businessNameRead = businessName ? (
    <span className="inline-flex items-center gap-2.5">
      <CompanyAvatar
        companyFiles={currentDeal.company_files}
        websiteUrl={website || undefined}
        alt={businessName}
        className="h-7 w-7 shrink-0 bg-background ring-1 ring-border"
        iconClassName="h-4 w-4"
      />
      <span className="text-base font-medium">{businessName}</span>
    </span>
  ) : null;

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <Legend />
        <div className="flex items-center gap-3">
          {!bulk.enabled && showReviewHead && (
            <ReviewHead
              pending={pendingCount}
              onVerifyAll={handleVerifyAll}
              // Spec: Verify all hides/disables while re-enrichment is in
              // flight to avoid stomping on the in-flight worker.
              isVerifying={isVerifyingAll || isReEnriching}
            />
          )}
          {bulk.enabled ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleSaveBulkEdit}
                disabled={isBulkSaving}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-foreground px-2.5 py-1 text-[11px] font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {isBulkSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Check className="h-3 w-3" />
                )}
                {isBulkSaving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={handleCancelBulkEdit}
                disabled={isBulkSaving}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground disabled:opacity-50"
              >
                <X className="h-3 w-3" />
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleStartBulkEdit}
              disabled={isReEnriching}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground disabled:opacity-50"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </button>
          )}
          <button
            type="button"
            onClick={handleReEnrich}
            disabled={isReEnriching || bulk.enabled}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors",
              isReEnriching
                ? "border-blue-200 bg-blue-50 text-blue-700 cursor-not-allowed dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300"
                : "border-border text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
              bulk.enabled && "opacity-50 cursor-not-allowed",
            )}
            title={
              isReEnriching
                ? "Re-enrichment in progress"
                : bulk.enabled
                  ? "Finish editing before re-enriching"
                  : "Re-run AI enrichment for this deal"
            }
          >
            {isReEnriching ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            {isReEnriching ? "Re-enriching…" : "Re-enrich"}
          </button>
        </div>
      </div>

      <Section title="Company">
        <EditableTextField
          label="Business Name"
          fieldName="company_name"
          value={businessName}
          readValue={businessNameRead}
          status={fieldStatus("company_name", !!overviewFallback(overview.name))}
          sources={dealFieldSources("company_name")}
        />
        <EditableTextareaField
          label="Description"
          fieldName="short_bio"
          value={description}
          status={fieldStatus("short_bio", !!overviewFallback(overview.description))}
          clampLines={3}
          sources={dealFieldSources("short_bio")}
        />
        <EditableSelectField
          label="Stage"
          fieldName="stage"
          value={stageValue || ""}
          options={StageOptions.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
          status={fieldStatus("stage", !!overviewStageFallback)}
          readValue={stageAiPlaceholder ? null : stageLabel}
          sources={dealFieldSources("stage")}
        />
        <div
          id="field-industries"
          className="scroll-mt-24 transition-[box-shadow,background-color] duration-300 data-[flash=true]:bg-purple-50/60 data-[flash=true]:ring-2 data-[flash=true]:ring-purple-400 data-[flash=true]:ring-inset dark:data-[flash=true]:bg-purple-950/30"
        >
          <EditableIndustryField
            subIndustryCodes={subIndustryCodes}
            status={fieldStatus(
              "sub_industries",
              (overview.industry?.length ?? 0) > 0,
              resolvableSubIndustryCodes,
            )}
            sources={dealFieldSources("sub_industries")}
          />
        </div>
        <EditableMultiBadgeField
          label="Business Model"
          fieldName="business_model"
          value={businessModelArray}
          options={BusinessModels.map((m) => ({
            value: m.value,
            label: m.label,
          }))}
          status={fieldStatus("business_model", !!overview.businessModel)}
          sources={dealFieldSources("business_model")}
          renderItemTooltip={(opt) => {
            const m = BusinessModels.find((bm) => bm.value === opt.value);
            if (!m) return null;
            return (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-primary-foreground/60">
                  Business Model
                </p>
                <p className="text-sm font-semibold">
                  {m.label} ({m.value})
                </p>
                {m.notes && <p>{m.notes}</p>}
                <div className="flex gap-3 text-[10px] text-primary-foreground/60">
                  <span>
                    Payer:{" "}
                    <span className="text-primary-foreground">{m.payer}</span>
                  </span>
                  <span>
                    Receiver:{" "}
                    <span className="text-primary-foreground">
                      {m.receiver}
                    </span>
                  </span>
                </div>
              </div>
            );
          }}
        />
        <EditableMultiBadgeField
          label="Revenue Model"
          fieldName="revenue_models"
          value={revenueModelArray}
          options={RevenueModels.map((m) => ({
            value: m.value,
            label: m.label,
          }))}
          status={fieldStatus("revenue_models", false)}
          sources={dealFieldSources("revenue_models")}
          renderItemTooltip={(opt) => {
            const m = RevenueModels.find((rm) => rm.value === opt.value);
            if (!m) return null;
            return (
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase tracking-wider text-primary-foreground/60">
                  Revenue Model
                </p>
                <p className="text-sm font-semibold">{m.label}</p>
                {m.description && <p>{m.description}</p>}
                {m.examples && m.examples.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {m.examples.map((ex) => (
                      <span
                        key={ex}
                        className="rounded bg-primary-foreground/15 px-1.5 py-0.5 text-[10px] text-primary-foreground"
                      >
                        {ex}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          }}
        />
        <EditableSourceField value={source} />
        <EditableComboboxField
          label="Country"
          fieldName="address"
          value={country}
          options={CountryOptions}
          status={fieldStatus("address", !!overviewFallback(overview.country))}
          sources={dealFieldSources("address")}
        />
        <div
          id="field-company_website"
          className="scroll-mt-24 transition-[box-shadow,background-color] duration-300 data-[flash=true]:bg-purple-50/60 data-[flash=true]:ring-2 data-[flash=true]:ring-purple-400 data-[flash=true]:ring-inset dark:data-[flash=true]:bg-purple-950/30"
        >
          <EditableTextField
            label="Website"
            fieldName="company_website"
            value={website}
            readValue={websiteRead}
            type="url"
            placeholder="https://…"
            status={fieldStatus("company_website", !!overviewFallback(overview.website))}
            sources={dealFieldSources("company_website")}
            suggestions={websiteSuggestions}
          />
        </div>
        <div
          id="field-linkedIn"
          className="scroll-mt-24 transition-[box-shadow,background-color] duration-300 data-[flash=true]:bg-purple-50/60 data-[flash=true]:ring-2 data-[flash=true]:ring-purple-400 data-[flash=true]:ring-inset dark:data-[flash=true]:bg-purple-950/30"
        >
          <EditableTextField
            label="LinkedIn"
            fieldName="linkedIn"
            value={linkedIn}
            readValue={linkedInRead}
            type="url"
            placeholder="https://linkedin.com/…"
            status={fieldStatus("linkedIn", false)}
            suggestions={linkedInSuggestions}
            sources={dealFieldSources("linkedIn")}
          />
        </div>
      </Section>

      <Section title="Fundraising">
        <EditableFundraisingField
          currentRound={{
            amount: currentRound.amount || "",
            currency: currentRound.currency || "",
          }}
          previousRounds={previousRounds}
          readChildren={
            currentRoundLabel || previouslyRaised || latestValuation ? (
              <>
                <ReadField
                  label="Current Round"
                  value={currentRoundLabel}
                  status={fieldStatus(
                    "current_funding_round",
                    !!currentRoundLabel,
                  )}
                />
                <ReadField
                  label="Previously Raised"
                  value={previouslyRaised}
                  status={fieldStatus(
                    "previous_funding_rounds",
                    !!previouslyRaised,
                  )}
                />
                <ReadField label="Valuation" value={latestValuation} />
              </>
            ) : (
              // No usable fundraising values — still surface the review
              // dots so the section reads as missing rather than absent.
              <div className="flex flex-col">
                <ReadField
                  label="Current Round"
                  value={null}
                  status={fieldStatus("current_funding_round", false)}
                />
                <ReadField
                  label="Previously Raised"
                  value={null}
                  status={fieldStatus("previous_funding_rounds", false)}
                />
              </div>
            )
          }
        />
      </Section>
    </div>
  );
}
